// Timezone choices for league setup.
//
// This used to be a free-text IANA field ("America/New_York"). Two problems with
// that: almost nobody knows what to type, and a single typo makes
// Intl.DateTimeFormat throw deep inside schedule building, which took down
// activation with no visible error. A fixed list removes both, and validation
// below is the backstop for any value that reaches the server another way.
//
// Drop times follow this zone, so it is worth getting right.

export interface TimeZoneChoice {
  id: string;
  label: string;
}

export const TIME_ZONES: TimeZoneChoice[] = [
  { id: "America/New_York", label: "Eastern time (New York, Atlanta, Miami)" },
  { id: "America/Chicago", label: "Central time (Chicago, Houston, Nashville)" },
  { id: "America/Denver", label: "Mountain time (Denver, Salt Lake City)" },
  { id: "America/Phoenix", label: "Arizona (no daylight saving)" },
  { id: "America/Los_Angeles", label: "Pacific time (Los Angeles, Seattle)" },
  { id: "America/Anchorage", label: "Alaska time" },
  { id: "Pacific/Honolulu", label: "Hawaii" },
  { id: "America/Puerto_Rico", label: "Puerto Rico" },
  { id: "America/Toronto", label: "Toronto" },
  { id: "America/Mexico_City", label: "Mexico City" },
  { id: "Europe/London", label: "London" },
  { id: "Europe/Paris", label: "Paris, Berlin, Madrid" },
  { id: "Africa/Lagos", label: "Lagos" },
  { id: "Africa/Johannesburg", label: "Johannesburg" },
  { id: "Asia/Tokyo", label: "Tokyo" },
  { id: "Australia/Sydney", label: "Sydney" },
];

const DEFAULT_ZONE = "America/New_York";

// Real check, not a pattern match: ask the platform whether it can use the zone.
// This is exactly the call that throws later during scheduling, so if it passes
// here the schedule can be built.
export function isValidTimeZone(id: unknown): boolean {
  if (typeof id !== "string" || !id.trim()) return false;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: id });
    return true;
  } catch {
    return false;
  }
}

export function labelForTimeZone(id: string): string {
  return TIME_ZONES.find((z) => z.id === id)?.label ?? id;
}

// The list to render. A league already set to a zone that is not on the list (an
// older league, or a zone we have not curated) keeps its own value as an option
// instead of being silently switched to Eastern the next time someone saves.
export function timeZoneOptions(current?: string): TimeZoneChoice[] {
  if (!current || !isValidTimeZone(current)) return TIME_ZONES;
  if (TIME_ZONES.some((z) => z.id === current)) return TIME_ZONES;
  return [{ id: current, label: current }, ...TIME_ZONES];
}

// What the phone says it is, when we can trust it.
export function detectTimeZone(): string {
  try {
    const guess = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return isValidTimeZone(guess) ? guess : DEFAULT_ZONE;
  } catch {
    return DEFAULT_ZONE;
  }
}

// "7:12 PM" in the chosen zone. Shown next to the picker so the commissioner can
// confirm at a glance that drops will land at a sane hour for the league.
export function currentTimeIn(id: string, now = new Date()): string {
  if (!isValidTimeZone(id)) return "";
  try {
    return new Intl.DateTimeFormat("en-US", {
      timeZone: id,
      hour: "numeric",
      minute: "2-digit",
    }).format(now);
  } catch {
    return "";
  }
}
